import { FC, useCallback, useMemo, useState } from "react";
import { LineItemDto, QuoteDto } from "../../../dtos";
import { Grid, IconButton, Menu, MenuItem, TableCell, Tooltip, Box } from "@mui/material";
import { AuthenticatedComponent, DataTableColumn, StandardListViewV2, useFailedUpdateSnackbar } from "../../../Components/CoreLib/library";
import { LineItemOrdersDialog } from "../Dialogs/LineItemOrdersDialog";
import { triaxReferenceNumberColumnKeySuffixes } from '../../../constants/triaxReferenceNumberColumnKeySuffixes';
import { LineItemTypes, QuoteType, formatCurrency, getBrokerRateFromLineItem, getDriverRateFromLineItem, getHaulingRateFromLineItem } from "../../../util";
import { useArchiveLineItemByIdMutation, useArchiveLineItemDeleteFromDatabaseByIdMutation, useGetLineItemQuoteSummaryLineItemsQuery, usePatchLineItemByIdMutation } from "../../../store/generated/generatedApi";
import { MoreVert, Warning } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { BilledToType } from "../../../dtos/enums/BilledToType";
import { MaterialLineItemDto } from "../../../dtos/generated/MaterialLineItemDto";

export interface IQuoteLineItemsViewProps {
    quote: QuoteDto;
}

export const QuoteLineItemsView: FC<IQuoteLineItemsViewProps> = (props) => {
    const { quote } = props;
    const navigate = useNavigate();
    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [editMenuAnchorEl, setEditMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [editMenuOpen, setEditMenuOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<LineItemDto>();
    const [deleteObject, { error, isError, reset }] = useArchiveLineItemDeleteFromDatabaseByIdMutation();
    const [archiveObject, { error: archiveError, isError: isArchiveError, reset: resetArchive }] = useArchiveLineItemByIdMutation();
    const [restoreObject, { error: restoreError, isError: isRestoreError, reset: resetRestore }] = usePatchLineItemByIdMutation();
    const [isOrdersDialogOpen, setIsOrdersDialogOpen] = useState(false);
    const [ordersDialogLineItemId, setOrdersDialogLineItemId] = useState<string | undefined>(undefined);

    useFailedUpdateSnackbar('line item', isError, reset, error);
    useFailedUpdateSnackbar('line item', isArchiveError, resetArchive, archiveError);
    useFailedUpdateSnackbar('line item', isRestoreError, resetRestore, restoreError);

    const isFobQuote = useCallback(() => {
        return quote?.type === QuoteType.FOB;
    }, [quote?.type]);

    const isHeader = useCallback((row: LineItemDto) => {
        if (isFobQuote()) {
            return false;
        }
        return (row.type === '' || row.type === LineItemTypes.SiteHauling);
    }, [isFobQuote]);

    const hasInactiveMaterialPrice = useCallback((row: LineItemDto) => {
        if (row.type === LineItemTypes.Material) {
            const materialLineItem = row as MaterialLineItemDto;
            return materialLineItem.materialPrice?.isActive === false;
        }
        return false;
    }, []);

    const renderDescriptionWithWarning = useCallback((row: LineItemDto) => {
        const description = isHeader(row) ? <strong style={{ fontSize: '24px' }}>{row.description}</strong> : row.description;
        
        if (hasInactiveMaterialPrice(row)) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="This line item uses an inactive material price. Please update to an active material price." arrow>
                        <Warning color="warning" fontSize="small" />
                    </Tooltip>
                    <span style={{ color: '#ed6c02' }}>{description}</span>
                </Box>
            );
        }
        
        return description;
    }, [isHeader, hasInactiveMaterialPrice]);

    const renderEditMenuButton = useCallback((row: LineItemDto) => {
        return (
            <AuthenticatedComponent
                requiredPermissions={['delete:lineItem']}
                children={
                    <TableCell padding='none' sx={{ border: 'none' }}>
                        <IconButton
                            size='small'
                            className="hover-content"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setEditMenuOpen(true);
                                setEditMenuAnchorEl(e.currentTarget);
                                setSelectedRow(row);
                            }}>
                            <MoreVert />
                        </IconButton>
                    </TableCell>
                }
            />
        );
    }, [setEditMenuOpen, setEditMenuAnchorEl, setSelectedRow]);

    const tableColumns: DataTableColumn<LineItemDto>[] = useMemo(() => {
        let columns: DataTableColumn<LineItemDto>[] = [
            { key: 'description', label: 'Description', sortKey: 'DESCRIPTION', unsorted: true, fieldMapper: renderDescriptionWithWarning },
            { key: 'site', label: 'Site', sortKey: 'SITE', unsorted: true, fieldMapper: (row) => row.site?.name },
            { key: 'type', label: 'Type', sortKey: 'TYPE', unsorted: true },
            { key: 'region', label: 'Region', sortKey: 'REGION', unsorted: true },
            { key: 'equipmentType', label: 'Equipment Type', sortKey: 'EQUIPMENT_TYPE', unsorted: true, fieldMapper: (row) => row.equipmentType?.type },
            { key: 'quoteRate', label: 'Quote Rate', sortKey: 'QUOTE_RATE', unsorted: true, align: 'right', fieldMapper: (row) => getQuoteRate(row) },
            { key: 'quantity', label: 'Quantity', sortKey: 'QUANTITY', align: 'right', unsorted: true, fieldMapper: (row) => row.quantity ? row.quantity?.toFixed(2) : '' },
            { key: 'unitOfMeasure', label: 'Units', sortKey: 'UNIT', align: 'right', unsorted: true },
            { key: 'salesPrice', label: 'Sales Price', sortKey: 'SALES_PRICE', unsorted: true, align: 'right', fieldMapper: (row) => row.salesPrice !== null ? formatCurrency(row.salesPrice) : '' },
            { key: 'status', label: 'Status', sortKey: 'STATUS', unsorted: true, align: 'right', fieldMapper: (row) => getStatus(row) },
            { key: '', label: '', sortKey: '', unsorted: true, fieldMapper: (row) => row.type !== '' ? renderEditMenuButton(row) : <div style={{ padding: '16px' }}></div> },
        ];

        if (!isFobQuote()) {
            const nonFobColumns: DataTableColumn<LineItemDto>[] = [
                { key: 'materialPriceValue', label: 'Site + Pricing', sortKey: 'MATERIAL_PRICE_VALUE', unsorted: true, align: 'right', fieldMapper: (row) => getMaterialPriceValue(row) },
                { key: 'driverRate', label: 'Driver Rate', sortKey: 'DRIVER_RATE', unsorted: true, align: 'right', fieldMapper: getDriverRateFromLineItem },
                { key: 'brokerRate', label: 'Broker Rate', sortKey: 'BROKER_RATE', unsorted: true, align: 'right', fieldMapper: getBrokerRateFromLineItem },
                { key: 'haulingRate', label: 'Hauling Rate', sortKey: 'HAULING_RATE', unsorted: true, align: 'right', fieldMapper: getHaulingRateFromLineItem }
            ];

            columns.splice(4, 0,
                ...nonFobColumns
            );
        }
        return columns;
    }, [isFobQuote, renderDescriptionWithWarning, renderEditMenuButton]);

    const handleAddMenuOpen = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            setAddMenuAnchorEl(event.currentTarget);
            setAddMenuOpen(true);
        }
    };

    const buildEditUrl = useCallback((lineItem: LineItemDto) => {
        if (lineItem.type === LineItemTypes.Material) {
            return `/quote/${quote?.id}/lineItem/material/${lineItem.id}`;
        } else if (lineItem.type === LineItemTypes.SiteHauling) {
            return `/quote/${quote?.id}/lineItem/siteHauling/${lineItem.id}`;
        } else if (lineItem.type === 'Flat Rate') {
            return `/quote/${quote?.id}/lineItem/siteHauling/${lineItem.siteHaulingLineItemIdRelatedToFlatRate}`;
        } else if (lineItem.type === LineItemTypes.Hourly) {
            return `/quote/${quote?.id}/lineItem/hourly/${lineItem.id}`;
        } else if (lineItem.type === LineItemTypes.Dump) {
            return `/quote/${quote?.id}/lineItem/dump/${lineItem.id}`;
        } else if (lineItem.type === LineItemTypes.Misc) {
            return `/quote/${quote?.id}/lineItem/misc/${lineItem.id}`;
        }
        return '/';
    }, [quote?.id]);

    const handleCreate = useCallback((type: string) => {
        if (type === LineItemTypes.Material) {
            navigate(`/quote/${quote?.id}/lineItem/material/create`);
        } else if (type === LineItemTypes.SiteHauling) {
            navigate(`/quote/${quote?.id}/lineItem/siteHauling/create`);
        } else if (type === LineItemTypes.Hourly) {
            navigate(`/quote/${quote?.id}/lineItem/hourly/create`);
        } else if (type === LineItemTypes.Dump) {
            navigate(`/quote/${quote?.id}/lineItem/dump/create`);
        } else if (type === LineItemTypes.Misc) {
            navigate(`/quote/${quote?.id}/lineItem/misc/create`);
        }
    }, [navigate, quote?.id]);

    const handleEdit = useCallback(() => {
        if (selectedRow) {
            navigate(buildEditUrl(selectedRow));
        }
        setSelectedRow(undefined);
        setEditMenuOpen(false);
    }, [navigate, buildEditUrl, selectedRow]);

    const handleDelete = useCallback(() => {
        if (selectedRow) {
            deleteObject({ id: selectedRow.id });
        }
        setSelectedRow(undefined);
        setEditMenuOpen(false);
    }, [deleteObject, selectedRow]);

    const handleArchive = useCallback(() => {
        if (selectedRow) {
            archiveObject({ id: selectedRow.id });
        }
        setSelectedRow(undefined);
        setEditMenuOpen(false);
    }, [archiveObject, selectedRow]);

    const handleRestore = useCallback(() => {
        if (selectedRow) {
            restoreObject({ id: selectedRow.id });
        }
        setSelectedRow(undefined);
        setEditMenuOpen(false);
    }, [restoreObject, selectedRow]);

    const handleViewOrders = useCallback(() => {
        if (selectedRow) {
            setOrdersDialogLineItemId(selectedRow.id);
            setIsOrdersDialogOpen(true);
        }
        setSelectedRow(undefined);
        setEditMenuOpen(false);
    }, [selectedRow]);

    const renderAddMenu = () => {
        return (<Menu
            anchorEl={addMenuAnchorEl}
            open={addMenuOpen}
            onClose={() => { setAddMenuOpen(false); }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}>
            <AuthenticatedComponent
                requiredPermissions={['create:lineItem']}
                children={
                    <>
                        <MenuItem onClick={() => handleCreate(LineItemTypes.Material)}>Material</MenuItem>
                        <MenuItem onClick={() => handleCreate(LineItemTypes.SiteHauling)}>Haul Rate</MenuItem>
                        <MenuItem onClick={() => handleCreate(LineItemTypes.Hourly)}>Hourly</MenuItem>
                        <MenuItem onClick={() => handleCreate(LineItemTypes.Dump)}>Dump</MenuItem>
                        <MenuItem onClick={() => handleCreate(LineItemTypes.Misc)}>Misc</MenuItem>
                    </>
                }
            />
        </Menu>);
    }

    const renderEditMenu = useCallback(() => {
        return (<Menu
            anchorEl={editMenuAnchorEl}
            open={editMenuOpen}
            onClose={() => {
                setEditMenuOpen(false);
                setSelectedRow(undefined);
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}>
            <AuthenticatedComponent
                requiredPermissions={['edit:lineItem']}
                children={
                    <>
                        <MenuItem onClick={() => handleEdit()}>Edit</MenuItem>
                        {selectedRow?.isActive ? <MenuItem onClick={() => handleArchive()} >Archive</MenuItem> : <MenuItem onClick={() => handleRestore()}>Restore</MenuItem>}
                    </>
                }
            />
            <AuthenticatedComponent
                requiredPermissions={['read:order']}
                children={
                    <MenuItem onClick={() => handleViewOrders()}>Orders</MenuItem>
                }
            />
            <AuthenticatedComponent
                requiredPermissions={['edit:lineItem']}
                children={
                    <MenuItem onClick={() => handleDelete()} disabled={!selectedRow?.canDelete}>Delete</MenuItem>
                }
            />
        </Menu>);
    }, [editMenuAnchorEl, editMenuOpen, selectedRow?.isActive, selectedRow?.canDelete, handleEdit, handleArchive, handleRestore, handleDelete, handleViewOrders])

    return (
        <Grid container direction='column' spacing={3}>
            <Grid item>
                {renderAddMenu()}
                {renderEditMenu()}
                {isOrdersDialogOpen && ordersDialogLineItemId && (
                    <LineItemOrdersDialog
                        open={isOrdersDialogOpen}
                        onClose={() => {
                            setIsOrdersDialogOpen(false);
                            setOrdersDialogLineItemId(undefined);
                        }}
                        lineItemId={ordersDialogLineItemId}
                        quote={quote}
                    />
                )}
                <StandardListViewV2
                    referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                    enableMultiSort
                    headerIcon={<></>}
                    headerTitle={''}
                    breadCrumbs={[]}
                    isPaginationVisible={false}
                    getDataQuery={useGetLineItemQuoteSummaryLineItemsQuery}
                    additionalQueryParameters={quote ? { quoteId: quote?.id } : undefined}
                    isShowActiveToggleVisible={false}
                    tableColumns={tableColumns}
                    entityNameSingular='Quote'
                    editIcon={<MoreVert />}
                    permissionName='quote'
                    handleAdd={handleAddMenuOpen}
                    handleRowClick={(id, record) => {
                        const lineItem = record as LineItemDto;
                        if (lineItem.type !== '') {
                            navigate(buildEditUrl(lineItem));
                        }
                    }}
                    disableSelection
                    hideMenu
                    hideAddButtonDivider
                />
            </Grid>
        </Grid>
    );
};

function getMaterialPriceValue(row: LineItemDto) {
    if (row.isBilledTo === BilledToType.Customer || row.isBilledTo === BilledToType.Custom) {
        return 'BTC';
    }
    return row.rate !== null ? formatCurrency(row.rate) : '';
};

function getStatus(row: LineItemDto) {
    if (!row.isActive) {
        return 'Archived';
    }
    return row.status;
};

function getQuoteRate(row: LineItemDto) {
    if (row.flatRateApplied) {
        return row.rate ? formatCurrency(row.rate) : '';
    } else {
        if (row.type === 'Flat Rate') {
            return row.haulingFlatRate !== null ? formatCurrency(row.haulingFlatRate) : '';
        } else if (row.type !== '') {
            return row.quoteRate !== null ? formatCurrency(row.quoteRate) : '';
        }
        return '';
    }
};
